import React from 'react';
import { Target, Heart, Compass, Users, Shield, Award, TrendingUp, CheckCircle, Zap, Clock, Globe, FileCheck } from 'lucide-react';
import TestimonialsSlider from '../components/TestimonialsSlider';
import SEO from '../components/SEO';

const About = () => {
  return (
    <div className="min-h-screen">
      <SEO 
        title="About Trademark Flare - Canadian IP Protection Experts"
        description="Discover Trademark Flare, your premier partner for Canadian trademark registration and brand protection through CIPO. 99% success rate with 15+ years of expertise."
        keywords="about trademark flare, Halifax trademark experts, CIPO registration services, Canadian brand protection"
      />
      {/* Hero Section */}
      <section className="relative pt-28 pb-24 overflow-hidden bg-gradient-to-br from-brand-600 to-brand-800">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1556761175-b413da4baf72"
            alt="Professional team meeting"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
              <span className="text-white font-semibold text-sm">🍁 Canadian Excellence Since 2010</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Trademark Flare
            </h1>
            <p className="text-xl md:text-2xl text-brand-100 leading-relaxed">
              Igniting brand protection excellence across Canada with unmatched CIPO expertise
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section - Image Left, Content Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-800 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1763550662603-78aa2f2033bf"
                alt="Professional business team"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="bg-brand-100 rounded-full p-2">
                    <Shield className="w-5 h-5 text-brand-600" />
                  </div>
                  <span className="font-bold text-gray-900">1000+ Brands Protected</span>
                </div>
                <p className="text-sm text-gray-600">Trusted across every Canadian province</p>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 rounded-full p-3 mb-4">
                <Target className="w-8 h-8 text-brand-600" />
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">Our Mission</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At Trademark Flare, we illuminate the path to comprehensive brand protection for Canadian businesses. We believe every entrepreneur deserves world-class trademark services without the traditional complexity or prohibitive costs.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Our mission is to demystify CIPO trademark registration while delivering exceptional expertise and personalized service. Based in Halifax, we're committed to protecting what matters most—your brand's identity, reputation, and future growth potential.
              </p>
              <div className="bg-gradient-to-br from-brand-50 to-brand-100 rounded-xl p-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-3xl font-bold text-brand-600 mb-1">99%</div>
                    <div className="text-sm text-gray-600">Approval Rate</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-brand-600 mb-1">24/7</div>
                    <div className="text-sm text-gray-600">Customer Service</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - Content Left, Image Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-brand-100 text-brand-700 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Why We're Different
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Maritime Excellence, National Impact
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                Unlike generic trademark services, we specialize exclusively in Canadian trademark law and CIPO procedures. Our team lives and breathes Canadian intellectual property protection.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Award className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">CIPO Specialists</h3>
                    <p className="text-gray-600 text-sm">Deep understanding of Canadian Intellectual Property Office requirements and procedures.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Fast & Efficient</h3>
                    <p className="text-gray-600 text-sm">Streamlined process with 24-48 hour response times and quick CIPO filing.</p>
                  </div>
                </div>

                <div className="flex items-start space-x-4 bg-white rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
                  <div className="bg-brand-600 rounded-lg p-3 flex-shrink-0">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Transparent Pricing</h3>
                    <p className="text-gray-600 text-sm">No hidden fees, clear pricing, and honest timelines from day one.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-400 to-brand-500 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1568992687947-868a62a9f521"
                alt="Team collaboration"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute top-6 left-6 bg-white/95 backdrop-blur-sm rounded-xl shadow-xl p-4">
                <div className="flex items-center space-x-3">
                  <div className="bg-green-500 rounded-full p-2">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">Trusted Service</div>
                    <div className="text-sm text-gray-600">Since Day One</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
              Our Foundation
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide everything we do for Canadian businesses
            </p>
          </div>

          <div className="md:grid md:grid-cols-3 md:gap-8 flex md:flex-none overflow-x-auto snap-x snap-mandatory gap-4 pb-4 md:pb-0 scrollbar-hide -mx-4 px-4 md:mx-0 md:px-0">
            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-brand-600 to-brand-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Client-Focused</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Your success is our success. We prioritize your needs and work tirelessly to ensure your trademark registration experience is smooth and successful. Every decision we make puts Canadian businesses first.
              </p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-green-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-green-600 to-green-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Compass className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Transparency</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                Clear pricing, honest timelines, and straightforward communication. No hidden fees, no surprises—just reliable service you can trust. We believe in complete honesty throughout the CIPO process.
              </p>
            </div>

            <div className="bg-gradient-to-br from-brand-50 to-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-brand-100 group min-w-full md:min-w-0 snap-center">
              <div className="bg-gradient-to-r from-brand-600 to-brand-800 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform">
                <Users className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">Excellence</h3>
              <p className="text-gray-600 leading-relaxed text-center">
                We maintain the highest standards of service quality, from our comprehensive searches to our expert CIPO filing procedures. Excellence isn't just a goal—it's our standard practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process/Approach - Image Left, Content Right */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-brand-600 to-brand-600 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1572021335469-31706a17aaef"
                alt="Professional team working"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-brand-600 to-brand-800 text-white rounded-xl shadow-xl p-6 max-w-xs">
                <FileCheck className="w-8 h-8 mb-2" />
                <div className="font-bold text-lg">Proven Process</div>
                <div className="text-sm text-brand-100">500+ successful registrations</div>
              </div>
            </div>

            <div>
              <div className="inline-block bg-brand-100 text-brand-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Our Methodology
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">How We Work</h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                Our battle-tested approach ensures maximum success rates and minimal stress for Canadian business owners seeking trademark protection.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Comprehensive Assessment</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We begin by thoroughly understanding your trademark needs, conducting detailed searches through CIPO's database to ensure your mark is unique and registrable across Canada.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Expert Preparation</h3>
                    <p className="text-gray-600 leading-relaxed">
                      Our experienced team prepares your application with meticulous attention to detail, ensuring all CIPO requirements are met for maximum approval chances with proper classification.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="bg-brand-600 text-white w-10 h-10 rounded-full flex items-center justify-center font-bold flex-shrink-0 shadow-lg">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Ongoing Support</h3>
                    <p className="text-gray-600 leading-relaxed">
                      We monitor your application, respond to any office actions, and keep you informed throughout the entire registration process until your trademark is secured and protected.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Canadian Coverage - Content Left, Image Right */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="inline-block bg-red-100 text-red-600 rounded-full px-4 py-2 mb-4 text-sm font-semibold">
                Coast to Coast
              </div>
              <h2 className="text-4xl font-bold text-gray-900 mb-6">
                Serving All of Canada
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                From Vancouver to St. John's, from Toronto to Yellowknife—we protect Canadian brands in every province and territory. Our expertise spans the entire country.
              </p>
              
              <div className="bg-gradient-to-br from-red-50 to-orange-50 rounded-2xl p-8 mb-6">
                <h3 className="font-bold text-gray-900 mb-4 text-xl flex items-center">
                  <Globe className="w-6 h-6 text-red-600 mr-2" />
                  Provinces & Territories We Serve:
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">British Columbia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Alberta</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Saskatchewan</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Manitoba</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Ontario</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Quebec</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">Nova Scotia</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">New Brunswick</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">All Territories</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-red-600 flex-shrink-0" />
                    <span className="text-gray-700 text-sm">And More!</span>
                  </div>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-brand-50 rounded-xl p-5">
                <TrendingUp className="w-6 h-6 text-brand-600 flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">National Protection</h4>
                  <p className="text-gray-600 text-sm">A single CIPO trademark registration protects your brand across all provinces and territories—one application, nationwide coverage.</p>
                </div>
              </div>
            </div>

            <div className="order-1 lg:order-2 relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-400 to-orange-400 rounded-2xl opacity-10 blur-2xl"></div>
              <img
                src="https://images.unsplash.com/photo-1714428586252-d72da2ece0a0"
                alt="Canadian skyline"
                className="relative rounded-2xl shadow-2xl w-full h-[450px] object-cover"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold text-red-600 mb-1">10+</div>
                <div className="text-gray-700 font-semibold">Provinces Served</div>
                <div className="text-sm text-gray-500 mt-1">Coast to coast coverage</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-gradient-to-r from-brand-600 to-brand-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-white mb-4">
              Trusted by Canadian Businesses
            </h2>
            <p className="text-xl text-brand-100">
              Our track record speaks for itself
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">500+</div>
              <div className="text-brand-100">Trademarks Filed</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">98%</div>
              <div className="text-brand-100">Approval Rate</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">350+</div>
              <div className="text-brand-100">Happy Clients</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-white mb-2">10+</div>
              <div className="text-brand-100">Provinces Served</div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <TestimonialsSlider />
    </div>
  );
};

export default About;
